import { dropShipHoldProblem, type DropShipHold } from './dropShip';

/**
 * The Shipment (piece 22; decisions 0061, 0073): a real record over a
 * polymorphic source pair, a Sales Order or a drop-ship Purchase Order, with
 * lines so that shipping four of ten is a shipment and not a change to the
 * order. The Ship Confirmation is the one code path that consumes stock, and
 * the ledger stream wraps it. Nothing here touches a table.
 */
export const SHIPMENT_SOURCE_TYPES = ['SalesOrder', 'PurchaseOrder'] as const;
export type ShipmentSourceTypeValue = (typeof SHIPMENT_SOURCE_TYPES)[number];
export type ShipmentStatusValue = 'Draft' | 'Packed' | 'Shipped' | 'Delivered' | 'Cancelled';

/** The source is a Sales Order or a drop-ship Purchase Order; a stock Purchase Order is received, never shipped. */
export function shipmentSourceProblem(sourceType: string, source: { purchaseFulfillmentMethod?: string | null }): string | null {
  if (!(SHIPMENT_SOURCE_TYPES as readonly string[]).includes(sourceType)) return `${sourceType} is not a source a Shipment ships against; the pair is a Sales Order or a drop-ship Purchase Order`;
  if (sourceType === 'PurchaseOrder' && source.purchaseFulfillmentMethod !== 'DropShip') return 'a stock purchase order is received into a site, not shipped; only a drop-ship order ships';
  return null;
}

export interface ShipSourceLine {
  id: string;
  itemId: string | null;
  quantity: number;
  /** Advanced by each shipment line, never past quantity. */
  quantityConsumed: number;
  unitId: string;
  /** 🔒 The part a drop-ship order not yet confirmed ships from the vendor (decision 1930); a Sales Order line alone carries it. */
  dropShipHold?: DropShipHold | null;
}

export interface ShipLineInput {
  sourceLineId: string;
  quantity: number;
}

export function remainingToShip(line: ShipSourceLine): number {
  return Math.max(0, line.quantity - line.quantityConsumed - (line.dropShipHold?.quantity ?? 0));
}

/** A partial shipment is a line quantity; a line past the source line's remainder, a zero, or a line off the order is refused. */
export function shipLinesProblem(sourceLines: readonly ShipSourceLine[], lines: readonly ShipLineInput[]): string | null {
  if (lines.length === 0) return 'a shipment carries at least one line; enter what is going out against the lines it is going out for';
  for (const line of lines) {
    const source = sourceLines.find((s) => s.id === line.sourceLineId);
    if (!source) return 'a shipment line names a line of the order it ships against; reload the order and ship against the lines it shows';
    if (!source.itemId) return 'a free-text line names no Item and does not ship; take it off this shipment, or give the order line an Item first';
    if (!Number.isFinite(line.quantity) || line.quantity <= 0) return 'a shipped quantity is above zero';
    if (line.quantity > remainingToShip(source) + 1e-9) {
      // 🔨 The vendor's part is refused by the purchase order's number, so nobody ships it twice (decision 1930).
      if (source.dropShipHold && source.dropShipHold.quantity > 0) return dropShipHoldProblem(`line ${source.id}`, source.dropShipHold, remainingToShip(source), line.quantity, 'ship');
      return `line ${source.id} has ${remainingToShip(source)} left to ship`;
    }
  }
  return null;
}

/** The lines a new shipment defaults to: every remainder on the order. */
export function remainingLines(sourceLines: readonly ShipSourceLine[]): ShipLineInput[] {
  return sourceLines.filter((s) => s.itemId && remainingToShip(s) > 0).map((s) => ({ sourceLineId: s.id, quantity: remainingToShip(s) }));
}

/** 🔨 A Sales Order shipment consumes stock; a drop shipment never touched it and posts nothing. */
export function shipmentPostsStock(sourceType: ShipmentSourceTypeValue): boolean {
  return sourceType === 'SalesOrder';
}

/** `shippedDate?` is empty while Draft or Packed and required from Shipped on. */
export function shippedDateProblem(status: ShipmentStatusValue, shippedDate: string | Date | null): string | null {
  const shippedOrLater = status === 'Shipped' || status === 'Delivered';
  if (shippedOrLater && !shippedDate) return `a ${status} shipment carries the date it shipped`;
  if (!shippedOrLater && shippedDate && status !== 'Cancelled') return `a ${status} shipment has not shipped and carries no date`;
  return null;
}

/**
 * The moves a shipment makes: Draft to Packed or Shipped, Packed to Shipped,
 * Shipped to Delivered, Cancelled from Draft or Packed. 🔨 A Shipped shipment
 * is never cancelled; the ledger is appended, and goods that come back are a
 * Credit Note's return.
 */
export function shipmentMoveProblem(from: ShipmentStatusValue, to: ShipmentStatusValue): string | null {
  const allowed: Record<ShipmentStatusValue, readonly ShipmentStatusValue[]> = {
    Draft: ['Packed', 'Shipped', 'Cancelled'],
    Packed: ['Shipped', 'Cancelled'],
    Shipped: ['Delivered'],
    Delivered: [],
    Cancelled: [],
  };
  if (allowed[from].includes(to)) return null;
  if (to === 'Cancelled') return 'a shipped shipment is not canceled; goods that come back are a Credit Note’s return';
  return `a shipment does not move from ${from} to ${to}`;
}

export const SHIPMENT_STATUS_AFTER_CONFIRMATION: ShipmentStatusValue = 'Shipped';

/**
 * The statuses a shipment carries once it has been confirmed. `Delivered` is
 * reachable from `Shipped` alone, so a delivered shipment was confirmed and its
 * slip is the one that travelled with the goods.
 */
export const SHIPMENT_STATUSES_AFTER_CONFIRMATION: readonly ShipmentStatusValue[] = [SHIPMENT_STATUS_AFTER_CONFIRMATION, 'Delivered'];

/**
 * 🔨 A packing slip belongs to a shipment that has been confirmed and to no
 * other (piece 27: *a Purchase Order's confirmed shipments each offer their
 * packing slip*). A Draft shipment carries no shipped date, no carrier and no
 * tracking reference, so a slip drawn from it is a page that says goods went out
 * when none did. One function holds it, so the page that offers the link and the
 * route that serves the sheet cannot come to disagree.
 */
export function packingSlipProblem(status: string): string | null {
  if (SHIPMENT_STATUSES_AFTER_CONFIRMATION.includes(status as ShipmentStatusValue)) return null;
  return 'a packing slip is drawn from a confirmed shipment; confirm the shipment first and the slip carries the date, the carrier and the tracking reference';
}

/** What the Ship Confirmation needs before it writes: the date, the tracking reference, and the origin site where stock is consumed. */
export function confirmationProblem(input: { shippedOn: string | null | undefined; trackingNumber: string | null | undefined; postsStock: boolean; originSiteId: string | null | undefined }): string | null {
  if (!input.shippedOn || !/^\d{4}-\d{2}-\d{2}$/.test(input.shippedOn)) return 'a Ship Confirmation carries the date the goods left; pick the date the goods went out';
  if (!input.trackingNumber || input.trackingNumber.trim().length === 0) return 'a Ship Confirmation carries the tracking reference; enter the carrier’s tracking number';
  if (input.postsStock && !input.originSiteId) return 'a shipment from stock names the site the goods left, and no dispatch origin is set to default it; choose the site here, or set a default dispatch origin in Settings';
  return null;
}

// ---- The shapes the API and the screen exchange ----

export interface NewShipmentRequest {
  sourceDocumentType: ShipmentSourceTypeValue;
  sourceDocumentId: string;
  /** Every remainder on the order when absent. */
  lines?: ShipLineInput[];
}

export interface ShipmentLineAnswer {
  id: string;
  sourceLineId: string;
  itemId: string;
  sku: string | null;
  itemName: string | null;
  quantity: number;
  unitName: string;
  /** What the source line still has to ship after this line, for a partial shipment's reading. */
  sourceQuantity: number;
  sourceConsumed: number;
}

export interface ShipmentAnswer {
  id: string;
  shipmentNumber: string;
  status: ShipmentStatusValue;
  sourceDocumentType: ShipmentSourceTypeValue;
  sourceDocumentId: string;
  sourceNumber: string;
  companyId: string;
  companyName: string;
  shipToSiteId: string;
  shipToLabel: string;
  /** 🔒 The site the stock left, written at the Ship Confirmation (decision 0502); empty on a shipment that moved no stock. */
  originSiteId: string | null;
  originLabel: string | null;
  shippedDate: string | null;
  carrier: string | null;
  trackingNumber: string | null;
  postsStock: boolean;
  /** The site the confirmation defaults to, `dispatch.defaultOriginSiteId`, when the shipment consumes stock. */
  defaultOriginSiteId: string | null;
  originSites: { id: string; label: string }[];
  lines: ShipmentLineAnswer[];
}
